import { useCallback, useEffect, useMemo, useState } from "react";
import { useD3LineChart } from "./useD3LineChart";
import {
  useActualPerformance,
  actualItemsToFactors,
  computeActualPeriodReturn,
} from "./useActualPerformance";
import type { FactorsChartMetric } from "../services/productFactorsService";
import { factorsToPerformanceMetrics } from "../services/productFactorsService";
import type { PerformanceChartDataResult } from "./usePerformanceChartData";
import {
  clipFactorsFromLaunch,
  getV3FilterWindowBounds,
  resolveLaunchClipMs,
  V3_INVESTOR_TIME_FILTER_OPTIONS,
  type V3TimeFilter,
} from "../components/performanceChartV3Utils";
import {
  buildMergedChartData,
  getAvailableFilters,
  type SeriesInput,
} from "./useStrategyComparisonChartData";
import { formatPercentage, formatSignedPercentage } from "../utils/numberUtils";

const ACTUAL_FIXED_FILTERS = V3_INVESTOR_TIME_FILTER_OPTIONS.map(
  (o) => o.value
) as V3TimeFilter[];

const ACTUAL_SERIES_ID = "performance";
const ACTUAL_SERIES_COLOR = "#E2E8F0";

export function useActualPerformanceChartData({
  isActive,
  adminUserId,
  authenticatedUserId,
  launchDate,
  isDarkMode = true,
}: {
  isActive: boolean;
  adminUserId?: string;
  authenticatedUserId?: string;
  /**
   * The shown user's `launch_date`. When supplied and not in the future, the
   * actual series is anchored to it (pre-launch rows dropped) across the chart
   * and the period-return table. Omit to keep full history.
   */
  launchDate?: string | null;
  isDarkMode?: boolean;
}): PerformanceChartDataResult {
  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useActualPerformance(adminUserId, isActive, authenticatedUserId);

  const [selectedPeriod, setSelectedPeriod] = useState<string>("Since inception");

  useEffect(() => {
    if (isActive) {
      setSelectedPeriod("Since inception");
    }
  }, [isActive]);

  const launchClipMs = useMemo(() => resolveLaunchClipMs(launchDate), [launchDate]);
  const factors = useMemo(
    () =>
      clipFactorsFromLaunch(
        data?.items ? actualItemsToFactors(data.items) : [],
        launchClipMs
      ),
    [data, launchClipMs]
  );

  const performanceMetrics: FactorsChartMetric[] = useMemo(
    () =>
      factors.length > 0
        ? factorsToPerformanceMetrics(factors, 0, "STRATEGY")
        : [],
    [factors]
  );

  const firstDate = factors.length > 0 ? factors[0].t : null;

  const v3LoadedCoverage = useMemo(
    () =>
      ACTUAL_FIXED_FILTERS.reduce<Record<V3TimeFilter, boolean>>(
        (acc, f) => {
          acc[f] = factors.length > 0;
          return acc;
        },
        {} as Record<V3TimeFilter, boolean>
      ),
    [factors]
  );

  const v3IntrinsicAvailability = useMemo(
    () => ({ ...v3LoadedCoverage }),
    [v3LoadedCoverage]
  );

  const filterMetricsByPeriod = useCallback(
    (
      metrics: FactorsChartMetric[],
      period: V3TimeFilter
    ): FactorsChartMetric[] => {
      if (!v3LoadedCoverage[period] || metrics.length === 0) return [];
      if (period === "All" || period === "Since inception") return metrics;

      const latestMetric = metrics[metrics.length - 1];
      if (!latestMetric) return [];

      const anchor = new Date(latestMetric.metricDate);
      const { start, end } = getV3FilterWindowBounds(period, anchor, firstDate);
      const endMs = end.getTime();

      if (!start)
        return metrics.filter(
          (m) => new Date(m.metricDate).getTime() <= endMs
        );

      const startMs = start.getTime();
      return metrics.filter((m) => {
        const ms = new Date(m.metricDate).getTime();
        return ms > startMs && ms <= endMs;
      });
    },
    [firstDate, v3LoadedCoverage]
  );

  const v3HasDataByPeriod = useMemo(() => {
    const factorMetrics = performanceMetrics.filter(
      (m) => m.metricName === "factor"
    );
    return ACTUAL_FIXED_FILTERS.reduce<Record<V3TimeFilter, boolean>>(
      (acc, period) => {
        acc[period] =
          filterMetricsByPeriod(factorMetrics, period).length > 0;
        return acc;
      },
      {} as Record<V3TimeFilter, boolean>
    );
  }, [filterMetricsByPeriod, performanceMetrics]);

  const series = useMemo<SeriesInput[]>(() => {
    if (factors.length === 0) return [];
    return [{
      id: ACTUAL_SERIES_ID,
      label: "Actual Performance",
      color: ACTUAL_SERIES_COLOR,
      factors,
      startAtUtc: null,
      todayWindow: "utc-date-inclusive",
    }];
  }, [factors]);

  const availableFilters = useMemo(
    () => getAvailableFilters(series, ACTUAL_FIXED_FILTERS.filter((f) => f !== "All")),
    [series]
  );

  const orderedFiltersForTable = availableFilters;

  const sortedFactors = useMemo(
    () =>
      [...factors].sort(
        (a, b) => new Date(a.t).getTime() - new Date(b.t).getTime()
      ),
    [factors]
  );

  // Shared with the dashboard widget (computeActualPerfMetrics) so the modal
  // table and the widget always show identical period returns.
  const timeframePerformance = useMemo(() => {
    const metrics: Record<string, number | null> = {};
    ACTUAL_FIXED_FILTERS.forEach((period) => {
      metrics[period] =
        sortedFactors.length > 0
          ? computeActualPeriodReturn(sortedFactors, period)
          : null;
    });
    return metrics;
  }, [sortedFactors]);

  const chartData = useMemo(
    () => buildMergedChartData(series, selectedPeriod as V3TimeFilter),
    [selectedPeriod, series]
  );

  const chartHeight = 560;

  const { svgRef, containerRef, tooltipRef } = useD3LineChart({
    data: chartData,
    lines: [
      {
        dataKey: ACTUAL_SERIES_ID,
        stroke: isDarkMode ? ACTUAL_SERIES_COLOR : "currentColor",
        strokeWidth: 2,
        label: "Performance",
        showDots: false,
      },
    ],
    isDarkMode,
    height: chartHeight,
    margins: { top: 8, right: 72, bottom: 24, left: 12 },
    yAxisFormatter: (value: number) => formatPercentage(value, 2),
    tooltipFormatter: (value: number) => formatSignedPercentage(value, 2),
    showLegend: false,
    renderingMode: "enhanced",
    useUtcDateLabels: true,
  });

  const handleSelectPeriod = useCallback((value: string) => {
    setSelectedPeriod(value);
  }, []);

  const formatLocalDateFn = useCallback((dateString: string) => {
    const datePart = dateString.includes("T")
      ? dateString.split("T")[0]
      : dateString;
    const [yyyy, mm, dd] = datePart.split("-").map(Number);
    const date =
      yyyy && mm && dd ? new Date(yyyy, mm - 1, dd) : new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  const error = queryError ? "Failed to load actual performance data." : null;

  return {
    loading: isLoading,
    error,
    factors,
    retry: () => {
      void refetch();
    },
    performanceMetrics,
    svgRef,
    containerRef,
    tooltipRef,
    chartHeight,
    chartData,
    timeFilterOptions: V3_INVESTOR_TIME_FILTER_OPTIONS,
    availableFilters,
    selectedPeriod,
    handleSelectPeriod,
    formattedCreateDate: firstDate ?? null,
    formatPerformance: (value: number) => formatSignedPercentage(value, 2),
    formatLocalDate: formatLocalDateFn,
    orderedFiltersForTable,
    timeframePerformance,
    v3IntrinsicAvailability,
    v3LoadedCoverage,
    v3HasDataByPeriod,
    isDarkMode,
    isV3Factors: true,
    entityType: "STRATEGY",
    entityName: "Strategy",
  };
}
